import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getMemberComplaintDetails,
    addMemberComplaintComment,
    uploadMemberComplaintAttachments,
    rateMemberComplaint,
} from '../../../redux/actions/SocietyPortal/MemberPortalAction/MemberPortalAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import webTokenStorer from '../../../utils/webStorer';
import { notify } from '../../../utils/notification';
import { usePageRefresh } from '../../../context/PageRefreshContext';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_MY_COMPLAINTS } from '../../../Routing/routes';
import { formatIstDateTime } from '../../../utils/helpers/istDate';

// The resident's view of one of their own complaints. Runs entirely on
// /api/member/complaints/:id* (member_portal.complaints): the staff
// ViewComplaint screen needs complaint.read and carries assign / status /
// internal-note controls a resident must never see. The backend already
// strips internal notes from this payload; this screen only renders what it
// is given, plus the three things a resident may do — attach, comment, rate.

const STATUS_STYLE = {
    PENDING: 'tw-bg-blue-100 tw-text-blue-700',
    IN_PROGRESS: 'tw-bg-amber-100 tw-text-amber-700',
    COMPLETED: 'tw-bg-emerald-100 tw-text-emerald-700',
    REOPENED: 'tw-bg-rose-100 tw-text-rose-700',
};

const PRIORITY_STYLE = {
    LOW: 'tw-bg-slate-100 tw-text-slate-600',
    MEDIUM: 'tw-bg-blue-50 tw-text-blue-600',
    HIGH: 'tw-bg-amber-50 tw-text-amber-700',
    URGENT: 'tw-bg-rose-50 tw-text-rose-700',
};

const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const fullName = (u) => u
    ? ([u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || 'Society team')
    : 'Society team';

const fmtDate = (d) => d
    ? formatIstDateTime(d)
    : '—';

const bytes = (n) => {
    if (!n) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const resolveAssetUrl = (p) => {
    if (!p) return '';
    if (/^https?:\/\//i.test(p)) return p;
    const base = process.env.REACT_APP_NODE_MOBILE_BASE_URL || '';
    return `${base.replace(/\/+$/, '')}${p.startsWith('/') ? '' : '/'}${p}`;
};

const isImage = (mime, name) => {
    if (mime && mime.startsWith('image/')) return true;
    return /\.(png|jpe?g|gif|webp)$/i.test(name || '');
};

// Mirrors the backend limits on the attachment endpoints so a bad pick is
// refused here rather than as a 400 after the upload.
const MAX_FILES = 5;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const FILE_ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt';
const ALLOWED_FILE_RE = /\.(png|jpe?g|gif|webp|bmp|svg|pdf|docx?|xlsx?|txt)$/i;
const isAllowedFile = (file) =>
    (file.type || '').startsWith('image/') || ALLOWED_FILE_RE.test(file.name || '');

const pickFiles = (picked, current) => {
    const badType = picked.filter(f => !isAllowedFile(f));
    const badSize = picked.filter(f => f.size > MAX_FILE_BYTES);
    if (badType.length) notify('error', 'Only image, PDF, Word, Excel, or TXT files are allowed');
    if (badSize.length) notify('error', 'Each attachment must be under 5MB');
    const accepted = picked.filter(f => isAllowedFile(f) && f.size <= MAX_FILE_BYTES);
    const seen = new Set(current.map(f => `${f.name}:${f.size}`));
    const merged = [...current];
    accepted.forEach(f => {
        const key = `${f.name}:${f.size}`;
        if (!seen.has(key)) { seen.add(key); merged.push(f); }
    });
    if (merged.length > MAX_FILES) {
        notify('error', `Too many files. Max ${MAX_FILES} attachments`);
        return merged.slice(0, MAX_FILES);
    }
    return merged;
};

const AttachmentTile = ({ att, compact = false }) => {
    const url = resolveAssetUrl(att.file_path);
    const img = isImage(att.mime_type, att.file_name);
    return (
        <a
            href={url}
            target="_blank"
            rel="noreferrer"
            title={att.file_name}
            className={`tw-flex tw-items-center tw-gap-2 tw-border tw-border-gray-100 tw-rounded-md tw-bg-white hover:tw-border-rose-200 ${compact ? 'tw-pr-2' : 'tw-p-2'}`}
        >
            {img ? (
                <img src={url} alt={att.file_name} className={compact ? 'tw-h-9 tw-w-9 tw-object-cover tw-rounded-l-md' : 'tw-h-12 tw-w-12 tw-object-cover tw-rounded-md'} />
            ) : (
                <span className={`tw-flex tw-items-center tw-justify-center tw-bg-slate-50 tw-text-slate-500 ${compact ? 'tw-h-9 tw-w-9 tw-rounded-l-md' : 'tw-h-12 tw-w-12 tw-rounded-md'}`}>
                    <Icon icon="mdi:file-outline" className="tw-text-lg" />
                </span>
            )}
            <span className="tw-min-w-0">
                <span className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-800 tw-truncate">{att.file_name}</span>
                {(att.size_bytes || att.file_size) ? <span className="tw-block tw-text-[10px] tw-text-gray-400">{bytes(att.size_bytes || att.file_size)}</span> : null}
            </span>
        </a>
    );
};

const FilePickerList = ({ files, onRemove }) => files.length > 0 && (
    <div className="tw-mt-2 tw-space-y-1.5">
        {files.map((f, i) => (
            <div key={`${f.name}:${f.size}`} className="tw-flex tw-items-center tw-gap-2 tw-px-2.5 tw-py-1.5 tw-bg-gray-50 tw-rounded-md tw-text-[11px]">
                <Icon icon="mdi:paperclip" className="tw-text-gray-400" />
                <span className="tw-flex-1 tw-truncate tw-text-gray-700">{f.name}</span>
                <span className="tw-text-gray-400">{bytes(f.size)}</span>
                <button type="button" onClick={() => onRemove(i)} className="tw-p-0.5 tw-rounded hover:tw-bg-gray-200 tw-text-gray-400">
                    <Icon icon="mdi:close" />
                </button>
            </div>
        ))}
    </div>
);

const MyComplaintDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();
    const currentUserId = webTokenStorer.getUserId();
    const attachInputRef = useRef(null);
    const commentInputRef = useRef(null);

    const {
        complaintDetailsData: c,
        complaintDetailsLoading,
        complaintAttachmentLoading,
        complaintCommentLoading,
        complaintRatingLoading,
    } = useSelector(state => state.MemberPortalReducer || {});

    const [attachFiles, setAttachFiles] = useState([]);
    const [commentText, setCommentText] = useState('');
    const [commentFiles, setCommentFiles] = useState([]);
    const [ratingValue, setRatingValue] = useState(0);
    const [ratingHover, setRatingHover] = useState(0);
    const [ratingFeedback, setRatingFeedback] = useState('');

    const refresh = () => dispatch(getMemberComplaintDetails({ id, societyId }));

    useEffect(() => {
        if (id) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, societyId]);
    usePageRefresh(refresh);

    const comments = useMemo(() => (Array.isArray(c?.comments) ? c.comments : []), [c]);
    const attachments = useMemo(() => (Array.isArray(c?.attachments) ? c.attachments : []), [c]);

    const raisedByMe = c && currentUserId != null && String(c.raised_by_user_id) === String(currentUserId);
    const canRate = c?.status === 'COMPLETED' && raisedByMe && !c.rating;

    const handleAttachSelected = (e) => {
        const picked = Array.from(e.target.files || []);
        if (attachInputRef.current) attachInputRef.current.value = '';
        if (picked.length) setAttachFiles(prev => pickFiles(picked, prev));
    };
    const handleUploadAttachments = () => {
        if (attachFiles.length === 0) return;
        dispatch(uploadMemberComplaintAttachments({
            id, files: attachFiles,
            onSuccess: () => { setAttachFiles([]); refresh(); },
        }));
    };

    const handleCommentSelected = (e) => {
        const picked = Array.from(e.target.files || []);
        if (commentInputRef.current) commentInputRef.current.value = '';
        if (picked.length) setCommentFiles(prev => pickFiles(picked, prev));
    };
    const canPostComment = Boolean(commentText.trim()) || commentFiles.length > 0;
    const handleAddComment = (e) => {
        e?.preventDefault?.();
        if (!canPostComment) return;
        dispatch(addMemberComplaintComment({
            id,
            comment_text: commentText.trim() || undefined,
            files: commentFiles,
            onSuccess: () => { setCommentText(''); setCommentFiles([]); refresh(); },
        }));
    };

    const handleRate = () => {
        if (!ratingValue) return;
        dispatch(rateMemberComplaint({
            id, rating: ratingValue, feedback: ratingFeedback.trim() || undefined,
            onSuccess: () => { setRatingValue(0); setRatingFeedback(''); refresh(); },
        }));
    };

    if (complaintDetailsLoading || (!c && complaintDetailsLoading !== false)) {
        return (
            <div className="tw-p-10 tw-flex tw-justify-center">
                <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-2 tw-border-gray-200 tw-border-t-rose-500" />
            </div>
        );
    }

    if (!c) {
        return (
            <div className="tw-p-5">
                <div className="tw-max-w-3xl tw-mx-auto tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-8 tw-text-center">
                    <Icon icon="mdi:file-search-outline" className="tw-text-3xl tw-text-gray-300 tw-mx-auto" />
                    <p className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mt-2">Complaint not found</p>
                    <p className="tw-text-[11px] tw-text-gray-500 tw-mt-1">It may belong to another flat, or it has been removed.</p>
                    <button
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_COMPLAINTS)}
                        className="tw-mt-4 tw-px-3 tw-py-1.5 tw-text-xs tw-rounded-md tw-border tw-border-gray-200 hover:tw-bg-gray-50"
                    >
                        Back to My Complaints
                    </button>
                </div>
            </div>
        );
    }

    // The complaint's own wing covers wing-level complaints, which have no flat.
    const wingTitle = c.wing?.title || c.wing?.number || c.flat?.wing?.title;
    const flatLabel = [
        wingTitle ? `Wing ${wingTitle}` : null,
        c.flat?.flat_number ? `Flat ${c.flat.flat_number}` : (c.flat_id || !wingTitle ? null : 'Common area'),
    ].filter(Boolean).join(' · ') || null;

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-max-w-3xl tw-mx-auto tw-space-y-4">
                <button
                    onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_COMPLAINTS)}
                    className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 hover:tw-text-gray-800"
                >
                    <Icon icon="mdi:arrow-left" /> Back to My Complaints
                </button>

                {/* Header */}
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                        <span className="tw-text-[11px] tw-font-mono tw-font-semibold tw-text-gray-500">{c.complaint_code}</span>
                        <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${STATUS_STYLE[c.status] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                            {humanize(c.status)}
                        </span>
                        <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${PRIORITY_STYLE[c.priority] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                            {humanize(c.priority)} priority
                        </span>
                        {c.category?.name && (
                            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-gray-500">
                                <Icon icon={c.category.icon || 'mdi:tag-outline'} /> {c.category.name}
                            </span>
                        )}
                    </div>
                    <h1 className="tw-text-base tw-font-bold tw-text-gray-800 tw-mt-2">{c.subject}</h1>
                    <p className="tw-text-xs tw-text-gray-700 tw-whitespace-pre-wrap tw-mt-2">{c.description}</p>
                    <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-4 tw-gap-3 tw-mt-4">
                        {[
                            ['Raised', fmtDate(c.created_at)],
                            ['Due by', c.due_date ? fmtDate(c.due_date) : 'Not set'],
                            ['Location', flatLabel || '—'],
                            ['Handled by', c.assignedTo ? fullName(c.assignedTo) : 'Not assigned yet'],
                        ].map(([k, v]) => (
                            <div key={k}>
                                <p className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-500 tw-font-semibold">{k}</p>
                                <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-mt-0.5">{v}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Rating — only the raiser, only once the ticket is completed */}
                {c.status === 'COMPLETED' && (c.rating || raisedByMe) && (
                    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
                        <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">
                            {c.rating ? 'Your rating' : 'How was the resolution?'}
                        </h2>
                        {c.rating ? (
                            <div className="tw-mt-2">
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    <span className="tw-flex">
                                        {[1, 2, 3, 4, 5].map(n => (
                                            <Icon key={n} icon={n <= c.rating ? 'mdi:star' : 'mdi:star-outline'} className="tw-text-2xl tw-text-amber-400" />
                                        ))}
                                    </span>
                                    <span className="tw-text-xs tw-text-gray-600">{c.rating}/5</span>
                                    {c.rated_at && <span className="tw-text-[10px] tw-text-gray-400">· submitted {fmtDate(c.rated_at)}</span>}
                                </div>
                                {c.rating_feedback && <p className="tw-text-xs tw-text-gray-700 tw-mt-2 tw-whitespace-pre-wrap">{c.rating_feedback}</p>}
                            </div>
                        ) : canRate && (
                            <div className="tw-mt-2">
                                <div className="tw-flex tw-items-center tw-gap-1" onMouseLeave={() => setRatingHover(0)}>
                                    {[1, 2, 3, 4, 5].map(n => (
                                        <button
                                            type="button"
                                            key={n}
                                            onClick={() => setRatingValue(n)}
                                            onMouseEnter={() => setRatingHover(n)}
                                            className="tw-p-0.5"
                                            aria-label={`${n} star${n === 1 ? '' : 's'}`}
                                        >
                                            <Icon icon={n <= (ratingHover || ratingValue) ? 'mdi:star' : 'mdi:star-outline'} className="tw-text-2xl tw-text-amber-400" />
                                        </button>
                                    ))}
                                </div>
                                <textarea
                                    rows={2}
                                    value={ratingFeedback}
                                    onChange={e => setRatingFeedback(e.target.value)}
                                    placeholder="Anything the team should know? (optional)"
                                    className="tw-w-full tw-mt-2 tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                                />
                                <button
                                    type="button"
                                    onClick={handleRate}
                                    disabled={!ratingValue || complaintRatingLoading}
                                    className="tw-mt-2 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50"
                                >
                                    Submit rating
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Attachments */}
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
                    <div className="tw-flex tw-items-center tw-justify-between">
                        <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">Photos & documents</h2>
                        <label className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-medium tw-text-rose-700 tw-cursor-pointer hover:tw-text-rose-800">
                            <Icon icon="mdi:paperclip" /> Add files
                            <input ref={attachInputRef} type="file" multiple accept={FILE_ACCEPT} className="tw-hidden" onChange={handleAttachSelected} />
                        </label>
                    </div>
                    {attachments.length === 0 ? (
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-2">Nothing attached yet.</p>
                    ) : (
                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-2 tw-mt-3">
                            {attachments.map(att => <AttachmentTile key={att.attachment_id || att.file_path} att={att} />)}
                        </div>
                    )}
                    <FilePickerList files={attachFiles} onRemove={(i) => setAttachFiles(prev => prev.filter((_, idx) => idx !== i))} />
                    {attachFiles.length > 0 && (
                        <button
                            type="button"
                            onClick={handleUploadAttachments}
                            disabled={complaintAttachmentLoading}
                            className="tw-mt-2 tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50"
                        >
                            {complaintAttachmentLoading && <span className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent" />}
                            <Icon icon="mdi:cloud-upload-outline" /> Upload {attachFiles.length} file{attachFiles.length === 1 ? '' : 's'}
                        </button>
                    )}
                </div>

                {/* Conversation */}
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
                    <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">Updates</h2>
                    {comments.length === 0 ? (
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-2">No updates yet. The society team will post here as they work on it.</p>
                    ) : (
                        <div className="tw-space-y-2 tw-mt-3">
                            {comments.map(cm => {
                                const mine = currentUserId != null && String(cm.user_id ?? cm.user?.user_id) === String(currentUserId);
                                return (
                                    <div key={cm.comment_id} className={`tw-border tw-rounded-lg tw-p-3 ${mine ? 'tw-bg-rose-50/40 tw-border-rose-100' : 'tw-bg-white tw-border-gray-100'}`}>
                                        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                                            <span className="tw-text-[11px] tw-font-bold tw-text-gray-800">{mine ? 'You' : fullName(cm.user)}</span>
                                            <span className="tw-text-[10px] tw-text-gray-400">{fmtDate(cm.created_at)}</span>
                                        </div>
                                        {cm.comment_text && <p className="tw-text-xs tw-text-gray-700 tw-whitespace-pre-wrap tw-mt-1">{cm.comment_text}</p>}
                                        {Array.isArray(cm.attachments) && cm.attachments.length > 0 && (
                                            <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mt-2">
                                                {cm.attachments.map(att => <AttachmentTile key={att.attachment_id || att.file_path} att={att} compact />)}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <form onSubmit={handleAddComment} className="tw-mt-4 tw-border-t tw-border-gray-100 tw-pt-3">
                        <textarea
                            rows={3}
                            value={commentText}
                            onChange={e => setCommentText(e.target.value)}
                            placeholder="Add an update, or answer a question from the team"
                            className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                        />
                        <FilePickerList files={commentFiles} onRemove={(i) => setCommentFiles(prev => prev.filter((_, idx) => idx !== i))} />
                        <div className="tw-flex tw-items-center tw-justify-between tw-mt-2">
                            <label className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-text-gray-600 tw-cursor-pointer hover:tw-text-gray-800">
                                <Icon icon="mdi:paperclip" /> Attach
                                <input ref={commentInputRef} type="file" multiple accept={FILE_ACCEPT} className="tw-hidden" onChange={handleCommentSelected} />
                            </label>
                            <button
                                type="submit"
                                disabled={!canPostComment || complaintCommentLoading}
                                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50"
                            >
                                {complaintCommentLoading && <span className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent" />}
                                <Icon icon="mdi:send" /> Post
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default MyComplaintDetail;
